/**
 * Plain-text diagnostics report, meant to be PASTED as-is.
 *
 * Born from a concrete case: the app stayed "syncing" forever and the
 * reason was that the PowerSync service wasn't even up. From the
 * screen there was no way to tell "won't connect" apart from "connects but is slow", and
 * describing it by hand loses half the data.
 *
 * SINGLE SOURCE. The first version received its own object while the screen
 * rendered its rows independently: two sources for the same thing, so adding a
 * row to the UI left the report incomplete without anyone noticing. Now
 * both consume the SAME sections array: the screen renders it, this
 * function serializes it, and the hint is computed by reading it.
 *
 * Each item carries three fields for different reasons:
 *   · `label`/`value` — already-translated text: what's shown and what's copied.
 *   · `status`        — only used by the UI for color; the report ignores it.
 *   · `raw`           — UNtranslated value, so the logic doesn't depend on the
 *                       language. Without it, the hint would stop firing as soon as
 *                       someone changed a translation.
 *
 * Does NOT include personal data: no email, no name, no token. Whoever builds
 * the sections is responsible for trimming identifiers.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable identifier, language-independent. Used by the logic. */
  key: string;
  /** Already-translated label. */
  label: string;
  /** Already-translated/formatted value, exactly as shown on screen. */
  value: string;
  /** UI-only. */
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on the language. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. Passed in; the clock is not read here (pure function). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Looks up by `key`, never by label: labels are translated. */
function buscar(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const encontrado = s.items.find((i) => i.key === key);
    if (encontrado) {
      return encontrado;
    }
  }
  return undefined;
}

/**
 * Actionable hint for the pattern that started all this: there's a session and
 * sync doesn't start. It's almost always the service being down, not the app, and
 * saying so here saves searching in the wrong place.
 */
function pista(sections: DiagnosticSection[]): string[] {
  const sesion = buscar(sections, "session");
  const sync = buscar(sections, "sync");
  if (
    sesion?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const partes: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const seccion of input.sections) {
    partes.push("", `## ${seccion.title}`);
    for (const item of seccion.items) {
      // An empty field is MARKED instead of omitted: in a pasted report, a
      // missing value and one that wasn't collected read the same unless it's stated which is which.
      partes.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
    }
  }

  partes.push(...pista(input.sections));
  return partes.join("\n");
}
